/* Boho V2 "Make it yours": expandable enhancement rows, "add to my visit", and a reminder while booking.
 * Markup: sections/boho-v2-service-enhance.liquid and sections/boho-v2-service-landing-enhancements.liquid
 * ([data-bv2-enx]) with rows from snippets/boho-v2-enhance-row.liquid ([data-bv2-enx-item]).
 * Without this script every explanation is printed open and booking links are unchanged.
 * Booking links stay plain <a> elements; the Boulevard widget (assets/boho-boulevard-booking.js, not
 * edited) still intercepts them. The reminder is written into the note line of Boho's booking dialog
 * ([data-boho-booking-note], phones) when it opens, or shown as a card while
 * iframe.blvd-booking-widget.open (desktop, not modal) exists; a MutationObserver watches both. Picks are kept per page in localStorage and announced as a
 * "boho:enhancement-select" CustomEvent on document (names only; no tag is installed).
 */
(() => {
  if (window.bohoV2Enhance) return;
  const roots = [...document.querySelectorAll('[data-bv2-enx]')];
  if (!roots.length) return;
  window.bohoV2Enhance = true;

  const root = roots[0];
  const label = (key, fallback) => root.getAttribute(`data-l-${key}`) || fallback;
  const L = {
    add: label('add', 'add to plan'),
    added: label('added', 'in your plan'),
    visit: label('visit', 'your plan'),
    clear: label('clear', 'clear my plan'),
    book: label('book', 'book [name]'),
    remind: label('remind', 'On the first screen, tap + next to:'),
    min: label('min', 'min'),
  };
  const base = {
    name: root.dataset.baseName || '',
    price: root.dataset.basePrice || '',
    min: root.dataset.baseMin || '',
  };
  const bookUrl = root.dataset.bookUrl || '';
  const KEY = `bv2-visit:${location.pathname.replace(/\/+$/, '') || '/'}`;
  const store = {
    get() {
      try {
        const value = JSON.parse(localStorage.getItem(KEY) || '[]');
        return Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
      } catch { return []; }
    },
    set(list) {
      try {
        if (list.length) localStorage.setItem(KEY, JSON.stringify(list));
        else localStorage.removeItem(KEY);
      } catch { /* storage blocked: picks last for this page view only */ }
    },
  };
  const svg = (d) => `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="${d}"/></svg>`;
  const CHEV = svg('M4 6l4 4 4-4');
  const PLUS = svg('M8 3v10M3 8h10');
  const CHECK = svg('M3 8.5l3.2 3L13 4.5');
  const CROSS = svg('M4 4l8 8M12 4l-8 8');

  /* Rows */
  const items = new Map();
  const seen = new Set();
  document.querySelectorAll('[data-bv2-enx] [data-bv2-enx-item]').forEach((item) => {
    const name = item.dataset.name;
    if (!name || seen.has(name)) return;
    seen.add(name);
    const head = item.querySelector('.bv2-enx__head');
    const line = item.querySelector('.bv2-enx__line');
    const panel = item.querySelector('.bv2-enx__panel');
    if (panel && head) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bv2-enx__head bv2-enx__btn';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', panel.id);
      while (head.firstChild) btn.appendChild(head.firstChild);
      btn.insertAdjacentHTML('beforeend', `<span class="bv2-enx__chev">${CHEV}</span>`);
      head.replaceWith(btn);
      panel.hidden = true;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
      });
    }
    if (item.dataset.mode === 'ask') return;
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'bv2-enx__add';
    add.setAttribute('aria-pressed', 'false');
    add.innerHTML = `<span class="bv2-enx__icon bv2-enx__icon--plus">${PLUS}</span><span class="bv2-enx__icon bv2-enx__icon--check">${CHECK}</span><span class="bv2-enx__add-off" aria-hidden="true"></span><span class="bv2-enx__add-on" aria-hidden="true"></span><span class="bv2-sr-only"></span>`;
    add.querySelector('.bv2-enx__add-off').textContent = L.add;
    add.querySelector('.bv2-enx__add-on').textContent = L.added;
    add.querySelector('.bv2-sr-only').textContent = `add ${name} to my plan`;
    add.addEventListener('click', () => toggle(name));
    line.appendChild(add);
    items.set(name, { name, blvd: item.dataset.blvd || name, price: item.dataset.price || '', min: item.dataset.min || '', item, add });
  });
  roots.forEach((r) => r.classList.add('bv2-enx-on'));

  let picks = store.get().filter((n) => items.has(n));

  function announce(action, name) {
    try {
      document.dispatchEvent(new CustomEvent('boho:enhancement-select', {
        detail: { action, name: name || null, selected: picks.slice(), page: location.pathname },
      }));
    } catch { /* old browsers */ }
  }

  function toggle(name) {
    const on = !picks.includes(name);
    picks = on ? picks.concat(name) : picks.filter((n) => n !== name);
    store.set(picks);
    render();
    announce(on ? 'add' : 'remove', name);
  }

  function clear(from) {
    picks = [];
    store.set(picks);
    render();
    announce('clear');
    const target = from === 'menu' ? root.querySelector('.bv2-enx__add')
      : from === 'dock' ? document.querySelector('[data-bv2-sds] a, .bv2-dock a')
        : document.querySelector('.bv2-sdh__actions a, .bv2-lph__actions a');
    target?.focus();
  }

  /* What the plan says. Picks are never sent to Boulevard: the guest taps + there themselves. */
  function plan() {
    const chosen = picks.map((n) => items.get(n)).filter(Boolean);
    if (!chosen.length) return null;
    let sum = 0;
    let basePriced = !!base.name;
    if (base.name) {
      if (base.price && !Number.isNaN(+base.price)) sum += +base.price; else basePriced = false;
    }
    chosen.forEach((c) => { if (c.price && !Number.isNaN(+c.price)) sum += +c.price; });
    const money = `$${sum.toLocaleString('en-US')}`;
    const where = base.name ? 'on the first booking screen' : 'in booking, after you choose your facial';
    let how;
    if (chosen.length === 1) {
      how = `You add ${chosen[0].blvd} yourself ${where}: tap + next to it.`;
    } else {
      how = `You add these yourself ${where}: tap + next to each`;
      how += chosen.some((c) => c.blvd !== c.name) ? ` (${chosen.map((c) => c.blvd).join(', ')}).` : '.';
    }
    const names = (base.name ? [base.name] : []).concat(chosen.map((c) => c.name));
    const cap = L.visit.charAt(0).toUpperCase() + L.visit.slice(1);
    return {
      chosen,
      detail: `${basePriced ? 'Estimated total' : 'Estimated add-ons'} ${money}. ${how}`,
      compact: `${cap}: ${names.join(' + ')} · est. ${money}${basePriced ? '' : ' in add-ons'} · add-ons are tapped in booking`,
    };
  }
  const bookLabel = L.book.includes('[name]') ? (base.name ? L.book.replace('[name]', base.name) : 'book now') : L.book;

  /* "Your plan": a card at the end of the list; a compact line under the hero's book button and above the phone bar */
  function chip(kind) {
    const el = document.createElement('div');
    el.className = `bv2 bv2-visit bv2-visit--${kind}`;
    el.hidden = true;
    if (kind === 'menu') {
      el.innerHTML = '<div class="bv2-visit__text"><span class="bv2-visit__label"></span><p class="bv2-visit__names"></p><p class="bv2-visit__detail"></p></div>'
        + (bookUrl ? '<a class="bv2-btn bv2-visit__book" rel="noopener"></a>' : '');
      el.querySelector('.bv2-visit__label').textContent = L.visit;
      const a = el.querySelector('.bv2-visit__book');
      if (a) { a.href = bookUrl; a.textContent = bookLabel; }
    } else {
      el.innerHTML = '<p class="bv2-visit__text"><span class="bv2-visit__value"></span></p>';
    }
    el.insertAdjacentHTML('beforeend', `<button type="button" class="bv2-visit__clear">${CROSS}</button>`);
    const x = el.querySelector('.bv2-visit__clear');
    x.setAttribute('aria-label', L.clear);
    x.addEventListener('click', () => clear(kind));
    return el;
  }
  const chips = [];
  const heroActions = document.querySelector('.bv2-sdh__actions, .bv2-lph__actions');
  let heroChip = null;
  if (heroActions) {
    heroChip = chip('hero');
    heroActions.after(heroChip);
    chips.push(heroChip);
  }
  const menuChips = [];
  document.querySelectorAll('[data-bv2-enx-slot]').forEach((slot) => {
    const c = chip('menu');
    slot.appendChild(c);
    chips.push(c);
    menuChips.push(c);
  });
  const dockChip = chip('dock');
  document.body.appendChild(dockChip);
  chips.push(dockChip);
  const live = document.createElement('p');
  live.className = 'bv2-sr-only';
  live.setAttribute('role', 'status');
  root.appendChild(live);

  const inView = new Set();
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? inView.add(e.target) : inView.delete(e.target)));
      placeDock();
    });
    [heroChip, ...menuChips].filter(Boolean).forEach((c) => io.observe(c));
  }

  function visibleBar() {
    for (const el of document.querySelectorAll('[data-bv2-sds], .bv2-dock')) {
      const cs = getComputedStyle(el);
      if (cs.display !== 'none' && cs.visibility !== 'hidden' && el.getBoundingClientRect().height > 0) return el;
    }
    return null;
  }
  function placeDock() {
    const bar = visibleBar();
    const otherShowing = [...inView].some((c) => !c.hidden);
    const show = !!bar && picks.length > 0 && !otherShowing;
    dockChip.hidden = !show;
    if (show) dockChip.style.bottom = `${Math.max(0, innerHeight - bar.getBoundingClientRect().top) + 8}px`;
  }

  function render() {
    const p = plan();
    items.forEach((it) => {
      const on = picks.includes(it.name);
      it.add.setAttribute('aria-pressed', String(on));
      it.item.classList.toggle('is-picked', on);
    });
    chips.forEach((c) => {
      if (c !== dockChip) c.hidden = !p;
      if (!p) return;
      if (c.classList.contains('bv2-visit--menu')) {
        const names = c.querySelector('.bv2-visit__names');
        names.textContent = '';
        if (base.name) names.append(base.name);
        p.chosen.forEach((ch, i) => {
          if (base.name || i) names.append(' + ');
          const b = document.createElement('b');
          b.textContent = ch.name;
          names.append(b);
        });
        c.querySelector('.bv2-visit__detail').textContent = p.detail;
      } else {
        c.querySelector('.bv2-visit__value').textContent = p.compact;
      }
    });
    live.textContent = p ? `${p.compact}. ${p.detail}` : '';
    placeDock();
    syncReminder();
  }

  /* Reminder while booking is open */
  const canPop = typeof HTMLElement.prototype.showPopover === 'function';
  function reminder(kind) {
    const el = document.createElement('div');
    el.className = `bv2 bv2-remind bv2-remind--${kind}`;
    if (canPop) el.setAttribute('popover', 'manual');
    else el.hidden = true;
    el.innerHTML = `<p><b>${L.remind}</b> <span class="bv2-remind__names"></span></p><button type="button" class="bv2-remind__x" aria-label="Dismiss reminder">${CROSS}</button>`;
    document.body.appendChild(el);
    return el;
  }
  const card = reminder('card');
  let cardDismissed = false;
  const isShown = (el) => (canPop ? el.matches(':popover-open') : !el.hidden);
  function show(el, on) {
    if (on === isShown(el)) return;
    if (canPop) { if (on) el.showPopover(); else el.hidePopover(); } else el.hidden = !on;
  }
  card.querySelector('.bv2-remind__x').addEventListener('click', () => { cardDismissed = true; syncReminder(); });

  /* Phones: the note line inside Boho's booking dialog (snippets/boho-boulevard-booking.liquid,
     [data-boho-booking-note], role="status"). Filled when the dialog opens, cleared when it closes. */
  let noteWasOpen = false;
  function syncReminder() {
    const dialog = document.querySelector('[data-boho-booking-dialog]');
    const note = dialog && dialog.querySelector('[data-boho-booking-note]');
    const dialogOpen = !!(dialog && dialog.open);
    const frame = document.querySelector('iframe.blvd-booking-widget.open');
    if (!frame) cardDismissed = false;
    const names = picks.filter((n) => items.has(n)).map((n) => items.get(n).blvd).join(', ');
    if (note) {
      if (dialogOpen && !noteWasOpen) {
        note.textContent = names ? `${L.remind} ${names}` : '';
        note.hidden = !names;
      } else if (!dialogOpen && noteWasOpen) {
        note.textContent = '';
        note.hidden = true;
      }
    }
    noteWasOpen = dialogOpen;
    card.querySelector('.bv2-remind__names').textContent = names;
    show(card, !dialogOpen && !!frame && !!names && !cardDismissed);
  }

  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; syncReminder(); placeDock(); });
  }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class'] });
  addEventListener('resize', placeDock);
  addEventListener('load', placeDock);

  render();
})();
